import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { Typography } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  type ModelPortfolioGroupBy,
} from '../createModelPortfolio/modelPortfolioGroupBy';
import {
  HouseholdIpsEditableColumn,
  type HouseholdIpsEdits,
} from '../householdIpsComparison/householdIpsEditableColumn';
import type {
  HouseholdIpsActuals,
  HouseholdIpsOptions,
  HouseholdIpsValues,
} from '../householdIpsComparison/householdIpsFields';

/**
 * The step's options are the shared household IPS set, re-exported under the
 * name the step and its tests already use.
 */
export type EditHouseholdModelPortfolioIpsOptions = HouseholdIpsOptions;
export type {
  HouseholdIpsActuals,
  HouseholdIpsValues,
  IpsFieldOption,
} from '../householdIpsComparison/householdIpsFields';

export type EditHouseholdModelPortfolioIpsVisualProps = {
  options: EditHouseholdModelPortfolioIpsOptions;
  initialValues?: HouseholdIpsValues;
  actuals?: HouseholdIpsActuals;
  loading?: boolean;
  onNext: (values: HouseholdIpsValues) => void | Promise<void>;
};

export const EditHouseholdModelPortfolioIpsVisual = ({
  options,
  initialValues,
  actuals,
  loading = false,
  onNext,
}: EditHouseholdModelPortfolioIpsVisualProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['workflowCompletions', 'client', 'components']);
  // Empty until the fields below report, which they do on mount. Seeding this
  // with the model would have the step hand back fields it never showed, as
  // though the advisor had typed what the model already said.
  const [edits, setEdits] = useState<HouseholdIpsEdits>({
    values: {},
    blocked: false,
  });
  // Grouping only decides which asset class level the actual weights are
  // totalled against, so it stays local rather than joining the draft that
  // later becomes the proposal payload.
  const [groupBy, setGroupBy] = useState<ModelPortfolioGroupBy>(
    DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  );

  return (
    <WorkflowCompletionForm
      onSubmit={() => onNext(edits.values)}
      testId='edit-household-model-portfolio-ips-form'
      submitButtonProps={{
        label: t('continue'),
        disabled: loading || edits.blocked,
        dataTestId: 'edit-household-model-portfolio-ips-continue-button',
      }}
    >
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {translateBackendString(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackendString(options?.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}

      <HouseholdIpsEditableColumn
        options={options}
        initialValues={initialValues}
        actuals={actuals}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        onChange={setEdits}
        disabled={loading}
      />
    </WorkflowCompletionForm>
  );
};

export default EditHouseholdModelPortfolioIpsVisual;
